import { FC } from 'react';
import { TriangleAlert } from 'lucide-react';

import { Input } from '@new/common/ui/Input/Input';
import { SectionHeading } from '@new/common/ui/SectionHeading/SectionHeading';
import { CourseRowsEditor } from '@new/features/transcripts/shared/components/CourseRowsEditor';
import type { CourseCategory } from '@new/features/transcripts/shared/types';
import type { ReviewController } from '../../hooks/useAddTranscript';

interface TranscriptReviewEditStepProps {
  add: ReviewController & { categories: CourseCategory[] };
}

export const TranscriptReviewEditStep: FC<TranscriptReviewEditStepProps> = ({ add }) => {
  const { review, aiError, categories } = add;

  return (
    <div className="flex flex-col gap-6">
      {aiError ? (
        <div className="flex items-start gap-2 rounded-md bg-(--danger-D100) p-3 text-sm text-(--danger-D700)">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <span>
            We couldn&apos;t read the file automatically ({aiError}). You can enter the details
            manually below.
          </span>
        </div>
      ) : null}

      <fieldset className="m-0! flex flex-col gap-4 border-0 p-0">
        <SectionHeading title="Basic information" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
              Institution
            </label>
            <Input
              value={review.institution}
              placeholder="e.g. Arizona State University"
              onChange={(v) => add.setInstitution(v)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
              Estimated credits (your estimate)
            </label>
            <Input<number | string>
              type="number"
              min={0}
              step={1}
              value={review.unofficialCredits ?? ''}
              onChange={(v) => add.setUnofficialCredits(v === '' ? null : Math.round(Number(v)))}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
              Institution URL (optional)
            </label>
            <Input
              value={review.institutionUrl}
              placeholder="e.g. https://asu.edu"
              onChange={(v) => add.setInstitutionUrl(v)}
            />
          </div>
        </div>
      </fieldset>

      <CourseRowsEditor
        courses={review.courses}
        categories={categories}
        onAdd={add.addCourse}
        onUpdate={add.updateCourse}
        onRemove={add.removeCourse}
        helpText="It's your responsibility to add every course and verify that the names, codes, and credits are correct before submitting."
      />
    </div>
  );
};
